import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  getPatientsList,
  deletePatientAction,
} from "../../../../actions/patientActions";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../../../components/shared/Table";
import { Box, Flex, Text, Spinner } from "@chakra-ui/react";
import ToastComponent from "../../../../components/partials/ToastComponent";
import { columnsDesktop } from "../columns/PatientListColumnsNew"; // Adjust the path according to your file structure
import { columnsMobile } from "../columns/PatientListColumnsNewMobile"; // Adjust the path according to your file structure
import { useTranslation } from "react-i18next";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import {
  CREATE_PATIENT_RESET,
  DELETE_PATIENT_RESET,
} from "../../../../constants/patientConstants";
import { primary, textSecondary } from "../../../../utils/colors";
import useRoleCheck from "../../../../hooks/useRoleCheck";
import useIsMobile from "../../../../hooks/useIsMobile";
import CreatePatientNew from "../sidepanels/CreatePatientNew";
import useThemeColors from "../../../../hooks/useThemeColors";
import PatientStats from "./partials/PatientStats";
import PatientStatsMobile from "./partials/PatientStatsMobile";
import SearchInput from "../../../../components/input/SearchInput";
import PatientsFilterPopover from "./partials/PatientsFilterPopover";
import ActiveFilter from "./partials/ActiveFilter";
import useDebounce from "../../../../hooks/useDebounce";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import TableContainerWindowed from "../../../../components/shared/Table/TableContainerWindowed";
import PageHeader from "../../../../components/layout/PageHeader";
import UserNoAccess from "../../../../components/partials/UserNoAccess";
// import PatientCardListMobile from "./PatientCardListMobile";
import DrawerNew from "../../../../components/menu/DrawerNew";
// import PatientCardMobile from "./partials/PatientCardMobile";
import CardList from "../../../../components/mobile/CardList";
import PatientCardMobileAlt from "./partials/PatientCardMobileAlt";

const PatientListNewNew = ({ onOpenPatient }) => {
  // GENERAL
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const translate = (key) => t(key);
  const { whiteMode, textPrimaryMode } = useThemeColors();
  const isMobile = useIsMobile();

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  // STATE
  const [userId, setUserId] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // State to control delete confirmation modal

  const [patientName, setPatientName] = useState("");

  // ROLES
  const isDoctor = useRoleCheck("employee_doctor");

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1); // Reset page to 1 after search
  };

  // FETCHING LOADER
  const [isFetching, setIsFetching] = useState(false);

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 30, 50];

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const getPatients = useSelector((state) => state.getPatients);
  const {
    loading,
    error,
    patients,
    pages,
    countsByStatus,
    countsByAgeGroup,
    countsByGender,
    genderPercentages,
    totalCount,
    totalCountBasedOnFilter,
  } = getPatients;

  const deletePatient = useSelector((state) => state.deletePatient);
  const { error: errorDelete, success: successDelete } = deletePatient;

  // CREATE PATIENT REDUX
  const createPatient = useSelector((state) => state.createPatient);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
  } = createPatient;

  //SORTING
  const [sortField, setSortField] = useState("patientID");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSortChange = useCallback(
    (column) => {
      // Toggle sort order if the same column is clicked, or default to ascending
      const newSortOrder =
        sortField === column && sortOrder === "asc" ? "desc" : "asc";

      // Update sort field and order
      setSortField(column);
      setSortOrder(newSortOrder);

      // setPage(1);
    },
    [sortField, sortOrder]
  );

  // SIDEPANELS
  const [createPatientSidePanel, setCreatePatientSidePanel] = useState(false);

  // FILTERING
  const [resetFilter, setResetFilter] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [filterParams, setFilterParams] = useState({
    minAge: null,
    maxAge: null,
    startDate: "",
    endDate: "",
    status: [],
    state: [],
  });

  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setPage(1);
    setIsFilterActive(true);
    setResetFilter(false); // Reset the reset state after applying the filter
    localStorage.setItem("filterSettings_patients", JSON.stringify(filters));

    const appliedMinAge = filters.minAge !== null ? filters.minAge : "";
    const appliedMaxAge = filters.maxAge !== null ? filters.maxAge : "";

    dispatch(
      getPatientsList(
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        appliedMinAge,
        appliedMaxAge,
        filters.state,
        filters.status,
        filters.startDate,
        filters.endDate,
        slug
      )
    );
  };

  // RESET FILTER
  const handleResetFilter = () => {
    const resetState = {
      minAge: null,
      maxAge: null,
      startDate: "",
      endDate: "",
      status: [],
      state: [],
    };
    setFilterParams(resetState);
    localStorage.setItem("filterSettings_patients", JSON.stringify(resetState)); // Reset localStorage

    // console.log("Resetting filters", resetState);
    // console.log(
    //   "Resetting filters",
    //   localStorage.getItem("filterSettings_patients")
    // );
    setResetFilter(true);
    setIsFilterActive(false);
    dispatch(
      getPatientsList(
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        resetState.minAge,
        resetState.maxAge,
        resetState.state,
        resetState.status,
        resetState.startDate,
        resetState.endDate,
        slug
      )
    );
  };

  // DELETE HANDLER
  const deletePatientHandler = (id) => {
    // console.log("Deleting patient with ID", id);
    dispatch(deletePatientAction(id, slug));
  };

  // SIDEPANEL HANDLERS
  const openCreatePatientSidePanelModal = () => {
    setCreatePatientSidePanel(true);
    document.body.classList.add("no-scroll");
  };

  const closeCreatePatientSidePanelModal = () => {
    setCreatePatientSidePanel(false);
    document.body.classList.remove("no-scroll");
  };

  // FETCH DATA
  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        getPatientsList(
          debouncedSearchTerm,
          page,
          pageSize,
          sortField,
          sortOrder,
          filterParams.minAge,
          filterParams.maxAge,
          filterParams.state,
          filterParams.status,
          filterParams.startDate,
          filterParams.endDate,
          slug
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    filterParams,
    // successCreate,
    successDelete,
    slug,
  ]);

  // TOASTS
  useEffect(() => {
    if (successDelete) {
      toastComponent.showToast(t("toasts.patientDelete"), "success", 3000);
      setPatientName("");
      setPage(1);
    } else if (errorDelete) {
      toastComponent.showToast(t("toasts.error"), "error", 3000);
    }

    dispatch({ type: DELETE_PATIENT_RESET });
  }, [successDelete, errorDelete]);

  const resetToDefaultSortAndFetch = () => {
    const defaultSortField = "patientID";
    const defaultSortOrder = "desc";

    setSortField(defaultSortField);
    setSortOrder(defaultSortOrder);
    setPage(1);
    setIsFilterActive(false);
    setFilterParams({});
    setSearchTerm("");

    dispatch(
      getPatientsList(
        "",
        1,
        pageSize,
        defaultSortField,
        defaultSortOrder,
        null,
        null,
        [],
        [],
        "",
        "",
        slug
      )
    );
  };

  useEffect(() => {
    if (successCreate) {
      toastComponent.showToast(t("toasts.patientCreate"), "success", 3000);

      // Close the sidepanel after creating a patient
      closeCreatePatientSidePanelModal();

      resetToDefaultSortAndFetch();
    } else if (errorCreate) {
      if (errorCreate === "errorEmailAlreadyExists") {
        toastComponent.showToast(
          t("toasts.patientCreateErrorEmailExists"),
          "error",
          3000
        );
      } else {
        toastComponent.showToast(t("toasts.patientCreateError"), "error", 3000);
      }
    }

    dispatch({ type: CREATE_PATIENT_RESET });
  }, [successCreate, errorCreate]);

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess = currentTenantAccess?.permissions?.patients?.on;
  const hasAccessToCreate = currentTenantAccess?.permissions?.patients?.create;
  const hasAccessToDelete = currentTenantAccess?.permissions?.patients?.delete;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    if (perms.patients?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug, navigate]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.patients")} />
      </>
    );

  // helper to build the meta object patient detail window expects
  const buildMetaFromPatient = (p) => ({
    id: p?._id || p?.id, // 🔑 required for dedupe
    name: p?.fullName || `${p?.firstName ?? ""} ${p?.lastName ?? ""}`.trim(),
    phone: p?.phone || p?.mobile || p?.contacts?.phone,
    age: p?.age ?? p?.demographics?.age,
    email: p?.email,
    state: p?.state,
    status: p?.status,
  });

  // LOADING
  const isLoading = loading || isFetching;

  // LOGS
  // console.log("Patients", patients);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.patients")}</title>
        <meta name="description" content={t("pageDescription.patients")} />
      </Helmet>

      {/* SIDEPANELS  */}
      {!isMobile ? (
        <SidePanel
          open={createPatientSidePanel}
          close={closeCreatePatientSidePanelModal}
          title={t("patient.createPatient")}
          noBorder={true}
        >
          <Flex
            w="100%"
            bg={whiteMode}
            minW={["100%", "800px"]}
            maxW={["100%", "800px"]}
          >
            {hasAccessToCreate ? (
              <CreatePatientNew
                close={closeCreatePatientSidePanelModal}
                tenant={tenant}
              />
            ) : (
              <UserNoAccess
                pageName={t("noAccess.createPatient")}
                p="0rem 0 0"
              />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={createPatientSidePanel}
          onClose={closeCreatePatientSidePanelModal}
          title={t("patient.createPatient")}
          size="full"
        >
          {hasAccessToCreate ? (
            <CreatePatientNew
              close={closeCreatePatientSidePanelModal}
              tenant={tenant}
              loading={loadingCreate}
            />
          ) : (
            <UserNoAccess pageName={t("noAccess.createPatient")} p="0rem 0 0" />
          )}
        </DrawerNew>
      )}

      {/* MODALS  */}

      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.patientDelete")}
          deleteTitle={t("patient.deletePatientTitle")}
          deleteName={patientName}
          warningLabel={t("patient.deleteWarning")}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            deletePatientHandler(userId); // Use `userId` set from `MoreButtonLite`
            setShowDeleteConfirmation(false);
          }}
        />
      )}

      {/* MAIN  */}
      <Box w="100%" minH="initial" pt="0px">
        <Flex
          direction="column"
          w="100%"
          p={["0rem 0rem 0rem", "0rem 0rem 0rem", "2rem 2rem 6rem"]}
        >
          <Flex
            direction="column"
            w="100%"
            h="100%"
            bg={whiteMode}
            borderRadius={["0rem", "0rem", "2rem"]}
            overflow="hidden"
          >
            <PageHeader
              label={t("patient.patients")}
              buttonHandler={openCreatePatientSidePanelModal}
              buttonLabel={t("patient.createPatient")}
              filter={
                <PatientsFilterPopover
                  onFilterApply={handleFilterApply}
                  isFilterActive={isFilterActive}
                  setIsFilterActive={setIsFilterActive}
                  filterStatusOptions="patient"
                  savedFiltersKey="filterSettings_patients"
                  resetFilter={resetFilter}
                  onResetComplete={() => setResetFilter(false)}
                />
              }
            />

            {!isMobile ? (
              <PatientStats
                loading={loading}
                countsByStatus={countsByStatus || {}}
                globalTotalCount={totalCount || 0}
                totalCountBasedOnFilter={totalCountBasedOnFilter || 0}
                countsByGender={countsByGender || {}}
                countsByAgeGroup={countsByAgeGroup || []}
                genderPercentages={genderPercentages || {}}
                isFilterActive={isFilterActive}
                searchTerm={searchTerm}
              />
            ) : (
              <PatientStatsMobile
                loading={loading}
                countsByStatus={countsByStatus || {}}
                globalTotalCount={totalCount || 0}
                totalCountBasedOnFilter={totalCountBasedOnFilter || 0}
                countsByGender={countsByGender || {}}
                countsByAgeGroup={countsByAgeGroup || []}
                genderPercentages={genderPercentages || {}}
                isFilterActive={isFilterActive}
                searchTerm={searchTerm}
              />
            )}

            {isFilterActive && (
              <ActiveFilter
                filterParams={filterParams}
                setIsFilterActive={setIsFilterActive}
                onFilterApply={handleFilterApply}
                onReset={handleResetFilter}
              />
            )}

            <SearchInput
              placeholder={t("patient.search")}
              onChange={onChangeSearchTitle}
              searchName={searchTerm}
              p={["0 1.5rem", "0 2rem"]}
            />

            {!isMobile ? (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={patients}
                      columns={
                        isMobile
                          ? columnsMobile(translate, isMobile)
                          : columnsDesktop(
                              slug,
                              translate,
                              navigate,
                              isMobile,
                              whiteMode,
                              textPrimaryMode,
                              setShowDeleteConfirmation,
                              setUserId,
                              setPatientName
                            )
                      }
                      noItemsTitle={t("noItems.noPatients")}
                      noItemsDescription={t("noItems.noPatientsText")}
                      noItemsButtonLabel={t("noItems.noPatientsButton")}
                      noItemsButtonHandler={openCreatePatientSidePanelModal}
                      noItemsButtonDisabled={isDoctor}
                      // onRowClick={(userId) => {
                      //   navigate(`${userId}`);
                      // }}
                      onRowClick={(patientId) => {
                        const patient =
                          patients?.find(
                            (p) => p._id === patientId || p.id === patientId
                          ) || null;

                        if (onOpenPatient) {
                          const meta = patient
                            ? buildMetaFromPatient(patient)
                            : { id: patientId };
                          onOpenPatient(meta); // ✅ open OS window
                        } else {
                          navigate(`${patientId}`); // fallback to route
                        }
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 1.5rem" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCountBasedOnFilter}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            ) : (
              <Flex
                w="100%"
                justify="center"
                align="flex-start"
                p="1rem 0rem"
                minH="800px"
              >
                {!isLoading ? (
                  <Box p="0" w="100%">
                    {patients?.length > 0 ? (
                      <>
                        <CardList
                          slug={slug}
                          data={patients}
                          loading={loading}
                          sortField={sortField}
                          sortOrder={sortOrder}
                          onSortChange={(field, order) => {
                            setSortField(field);
                            setSortOrder(order);
                            setPage(1); // restart pagination
                          }}
                          page={page}
                          pages={pages}
                          pageSize={pageSize}
                          pageSizes={pageSizes}
                          setPage={setPage}
                          setPageSize={setPageSize}
                          totalCount={totalCountBasedOnFilter}
                          navigate={navigate}
                          title={t("patient.patients")}
                          noItemsText={t("noItems.noPatients")}
                          renderItem={(patient) => (
                            <PatientCardMobileAlt
                              key={patient._id}
                              patient={patient}
                              onRowClick={() =>
                                navigate(`/${slug}/patients/${patient._id}`)
                              }
                              onDelete={() => {
                                setUserId(patient._id);
                                setShowDeleteConfirmation(true);
                                setPatientName(
                                  `${patient.lastName} ${patient.firstName}`
                                );
                              }}
                            />
                          )}
                          selectOptions={
                            <>
                              <option value="patientID-desc">
                                {t("sort.recentFirst")}
                              </option>
                              <option value="patientID-asc">
                                {t("sort.oldestFirst")}
                              </option>
                              <option value="lastName-asc">
                                {t("sort.lastNameAZ")}
                              </option>
                              <option value="lastName-desc">
                                {t("sort.lastNameZA")}
                              </option>
                            </>
                          }
                        />
                      </>
                    ) : (
                      <Text color={textSecondary}>
                        {t("noItems.noPatients")}
                      </Text>
                    )}
                  </Box>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
    </>
  );
};

export default PatientListNewNew;
